// src/components/Agency/agencyCreatorTabs/AgencyCreatorPayouts.jsx

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Stack,
  Grow,
  Fade,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Tooltip,
  IconButton,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  AttachMoney as AttachMoneyIcon,
  Schedule as ScheduleIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  AccountBalance as AccountBalanceIcon,
  Description as DescriptionIcon,
  Campaign as CampaignIcon,
  TrendingUp as TrendingUpIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useMutation, useQueryClient } from 'react-query';
import API from '../../../API';
import { useCreator } from '../../../Hooks/agencySelectedCreatorContext';

const statusColorMap = {
  pending: 'warning',
  approved: 'info',
  paid: 'success',
  funded: 'success',
  cancelled: 'error',
  processing: 'info',
};

const statusIconMap = {
  pending: PendingIcon,
  approved: CheckCircleIcon,
  paid: CheckCircleIcon,
  funded: CheckCircleIcon,
  cancelled: CancelIcon,
  processing: PendingIcon,
};

const ModernCard = ({ children, ...props }) => {
  const theme = useTheme();
  
  return (
    <Card
      {...props}
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
        backdropFilter: 'blur(20px)',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        borderRadius: 3,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 12px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.primary.main})`,
        }
      }}
    >
      {children}
    </Card>
  );
};

const GlassmorphicPaper = ({ children, ...props }) => {
  const theme = useTheme();
  
  return (
    <Paper
      {...props}
      sx={{
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.95)} 100%)`,
        backdropFilter: 'blur(20px)',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        borderRadius: 2,
        ...props.sx
      }}
    >
      {children}
    </Paper>
  );
};

export default function AgencyCreatorPayouts() {
  const theme = useTheme();
  const { creatorData } = useCreator();
  const payouts = creatorData?.payouts || [];
  const queryClient = useQueryClient();

  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('NET30');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [statusFilter, setStatusFilter] = useState('active');
  const [claimingPayoutId, setClaimingPayoutId] = useState(null);

  // Payment confirmation mutation
  const confirmMutation = useMutation(
    ({ payoutId, method }) => API.agency.approvePayment(payoutId, { paymentMethod: method }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['agency', 'creator', creatorData?.creator?.creator]);
        setSnackbarMessage('Payment confirmed successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      },
      onError: (error) => {
        setSnackbarMessage(`Error confirming payment: ${error.message}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      },
    }
  );

  // Invoice claim mutation
  const claimMutation = useMutation(
    (invoiceId) => API.agency.claimInvoiceForCreator(invoiceId),
    {
      onMutate: (invoiceId) => {
        setClaimingPayoutId(invoiceId);
      },
      onSuccess: (response) => {
        setSnackbarMessage(
          `Successfully claimed ${response.amount_formatted || response.amount} - New balance: ${response.new_balance_formatted || response.new_balance}`
        );
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        // Refetch creator data to update the list
        queryClient.invalidateQueries(['agency', 'creator', creatorData?.creator?.creator]);
        setClaimDialogOpen(false);
        setSelectedPayout(null);
      },
      onError: (error) => {
        setSnackbarMessage(error?.response?.data?.error || 'Failed to claim invoice');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      },
      onSettled: () => {
        setClaimingPayoutId(null);
      }
    }
  );

  // Calculate summary statistics
  const statistics = useMemo(() => {
    const activePayouts = payouts.filter(p => p.status?.toLowerCase() !== 'cancelled');
    
    const stats = {
      total: activePayouts.length,
      pending: 0,
      approved: 0,
      paid: 0,
      funded: 0,
      claimable: 0,
      cancelled: payouts.filter(p => p.status?.toLowerCase() === 'cancelled').length,
      totalAmount: 0,
      pendingAmount: 0,
      paidAmount: 0,
      claimableAmount: 0,
    };

    activePayouts.forEach(payout => {
      const amount = parseFloat(payout.amount) || 0;
      stats.totalAmount += amount;

      const status = payout.status?.toLowerCase();
      if (status === 'pending') {
        stats.pending++;
        stats.pendingAmount += amount;
      } else if (status === 'approved' || status === 'processing') {
        stats.approved++;
      } else if (status === 'paid' || status === 'funded') {
        if (status === 'paid') stats.paid++;
        if (status === 'funded') stats.funded++;
        stats.paidAmount += amount;
        
        // Check if claimable (paid/funded and not already claimed and not withdrawn)
        const isClaimed = payout.metadata?.claimed_by_agency;
        const isWithdrawn = payout.withdrawal_id;
        if (!isClaimed && !isWithdrawn && payout.invoice_id) {
          stats.claimable++;
          stats.claimableAmount += amount;
        }
      }
    });

    return stats;
  }, [payouts]);

  // Filter payouts based on status
  const filteredPayouts = useMemo(() => {
    if (statusFilter === 'active') {
      return payouts.filter(p => p.status?.toLowerCase() !== 'cancelled');
    } else if (statusFilter === 'claimable') {
      return payouts.filter(p => {
        const status = p.status?.toLowerCase();
        return (status === 'paid' || status === 'funded') && 
               !p.metadata?.claimed_by_agency &&
               !p.withdrawal_id &&
               p.invoice_id;
      });
    } else if (statusFilter === 'all') {
      return payouts;
    } else {
      return payouts.filter(p => p.status?.toLowerCase() === statusFilter);
    }
  }, [payouts, statusFilter]);

  const handleOpenActionDialog = (payout) => {
    setSelectedPayout(payout);
    setPaymentMethod('NET30');
    setActionDialogOpen(true);
  };

  const handleCloseActionDialog = () => {
    setSelectedPayout(null);
    setPaymentMethod('NET30');
    setActionDialogOpen(false);
  };

  const handleConfirmAction = () => {
    if (!selectedPayout) return;

    confirmMutation.mutate({
      payoutId: selectedPayout.id,
      method: paymentMethod,
    });

    handleCloseActionDialog();
  };

  const handleOpenClaimDialog = (payout) => {
    setSelectedPayout(payout);
    setClaimDialogOpen(true);
  };

  const handleCloseClaimDialog = () => {
    setSelectedPayout(null);
    setClaimDialogOpen(false);
  };

  const handleConfirmClaim = () => {
    if (selectedPayout?.invoice_id) {
      claimMutation.mutate(selectedPayout.invoice_id);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const getStatusChip = (status) => {
    const normalizedStatus = status?.toLowerCase();
    const color = statusColorMap[normalizedStatus] || 'default';
    const StatusIcon = statusIconMap[normalizedStatus] || ReceiptIcon;
    
    return (
      <Chip 
        icon={<StatusIcon />}
        label={status?.charAt(0).toUpperCase() + status?.slice(1) || 'N/A'} 
        color={color} 
        size="small" 
        sx={{
          fontWeight: 600,
          borderRadius: 2,
          '& .MuiChip-label': {
            px: 1.5
          }
        }}
      />
    );
  };

  const isClaimable = (payout) => {
    const status = payout.status?.toLowerCase();
    return (status === 'paid' || status === 'funded') && 
           !payout.metadata?.claimed_by_agency &&
           !payout.withdrawal_id &&  // Creator hasn't withdrawn yet
           payout.invoice_id;  // Has associated invoice
  };

  if (!payouts || payouts.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <GlassmorphicPaper sx={{ p: 4, textAlign: 'center' }}>
          <ReceiptIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" color="text.secondary">
            No payouts found for this creator
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Payouts will appear here once they are created
          </Typography>
        </GlassmorphicPaper>
      </Box>
    );
  }

  return (
    <Fade in timeout={600}>
      <Box sx={{ p: 3 }}>
        {/* Info Banner for Claimable Payouts */}
        {statistics.claimable > 0 && (
          <Alert 
            severity="info" 
            icon={<InfoIcon />}
            sx={{ mb: 3, borderRadius: 2 }}
            action={
              <Button 
                color="inherit" 
                size="small"
                onClick={() => setStatusFilter('claimable')}
              >
                View Claimable
              </Button>
            }
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              💰 {statistics.claimable} payouts (${statistics.claimableAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}) are ready to claim
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Claim paid invoices to transfer funds directly to your agency balance. Once withdrawn by the creator or claimed by you, the invoice can no longer be claimed.
            </Typography>
          </Alert>
        )}

        {/* Statistics Summary */}
        <GlassmorphicPaper 
          elevation={0} 
          sx={{ 
            mb: 3,
            p: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary
              }}
            >
              Payout Summary
            </Typography>
            <Tooltip title="Claim paid invoices to transfer funds to your agency balance">
              <IconButton size="small">
                <InfoIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, background: alpha(theme.palette.success.main, 0.1), borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Total Amount
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.success.main, mt: 0.5 }}>
                  ${statistics.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {statistics.total} payouts
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper 
                sx={{ 
                  p: 2, 
                  background: alpha(theme.palette.info.main, 0.1), 
                  borderRadius: 2,
                  cursor: statistics.claimable > 0 ? 'pointer' : 'default',
                  transition: 'all 0.3s',
                  '&:hover': statistics.claimable > 0 ? {
                    transform: 'scale(1.05)',
                    boxShadow: `0 8px 16px ${alpha(theme.palette.info.main, 0.2)}`
                  } : {}
                }}
                onClick={() => statistics.claimable > 0 && setStatusFilter('claimable')}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Claimable Now
                  </Typography>
                  <AccountBalanceIcon sx={{ color: theme.palette.info.main, fontSize: 20 }} />
                </Stack>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.info.main, mt: 0.5 }}>
                  ${statistics.claimableAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {statistics.claimable} ready to claim
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, background: alpha(theme.palette.warning.main, 0.1), borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Pending
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.warning.main, mt: 0.5 }}>
                  ${statistics.pendingAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {statistics.pending} payouts
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, background: alpha(theme.palette.success.main, 0.1), borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Paid/Funded
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.success.main, mt: 0.5 }}>
                  {statistics.paid + statistics.funded}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  payouts completed
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Status Filter Chips */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              label="Active"
              onClick={() => setStatusFilter('active')}
              color={statusFilter === 'active' ? 'primary' : 'default'}
              sx={{ fontWeight: 600 }}
            />
            {statistics.claimable > 0 && (
              <Chip
                label={`💰 Claimable (${statistics.claimable})`}
                onClick={() => setStatusFilter('claimable')}
                color={statusFilter === 'claimable' ? 'info' : 'default'}
                sx={{ fontWeight: 600 }}
              />
            )}
            <Chip
              label="All"
              onClick={() => setStatusFilter('all')}
              color={statusFilter === 'all' ? 'primary' : 'default'}
              sx={{ fontWeight: 600 }}
            />
            <Chip
              label={`Pending (${statistics.pending})`}
              onClick={() => setStatusFilter('pending')}
              color={statusFilter === 'pending' ? 'warning' : 'default'}
              sx={{ fontWeight: 600 }}
            />
            <Chip
              label={`Paid (${statistics.paid + statistics.funded})`}
              onClick={() => setStatusFilter('paid')}
              color={statusFilter === 'paid' ? 'success' : 'default'}
              sx={{ fontWeight: 600 }}
            />
            {statistics.cancelled > 0 && (
              <Chip
                label={`Cancelled (${statistics.cancelled})`}
                onClick={() => setStatusFilter('cancelled')}
                color={statusFilter === 'cancelled' ? 'error' : 'default'}
                sx={{ fontWeight: 600 }}
              />
            )}
          </Stack>
        </GlassmorphicPaper>

        {/* Payouts Grid */}
        <Grid container spacing={3}>
          {filteredPayouts.length === 0 ? (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <ReceiptIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" color="text.secondary">
                  No {statusFilter === 'active' ? 'active' : statusFilter} payouts found
                </Typography>
              </Box>
            </Grid>
          ) : (
            filteredPayouts.map((payout, index) => (
              <Grow
                in
                timeout={600 + index * 100}
                key={payout.id}
              >
                <Grid item xs={12} sm={6} md={4}>
                  <ModernCard>
                    <CardContent>
                      {/* Header with Status */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          icon={<ReceiptIcon />}
                          label="Payout"
                          size="small"
                          color="success"
                          sx={{
                            fontWeight: 600,
                            borderRadius: 2
                          }}
                        />
                        {getStatusChip(payout.status)}
                        {payout.metadata?.claimed_by_agency && (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label="Claimed"
                            size="small"
                            color="info"
                            sx={{ fontWeight: 600, borderRadius: 2 }}
                          />
                        )}
                      </Box>

                      {/* PO Number / Title */}
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          mb: 2,
                          fontSize: '1.1rem',
                          lineHeight: 1.3,
                        }}
                      >
                        {payout.po_number || `Payout #${payout.id}`}
                      </Typography>

                      {/* Details Stack */}
                      <Stack spacing={1.5}>
                        {/* Amount */}
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <AttachMoneyIcon fontSize="small" sx={{ mr: 1, opacity: 0.7, color: 'text.secondary' }} />
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 700, 
                              color: theme.palette.success.main 
                            }}
                          >
                            ${parseFloat(payout.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                        </Box>

                        {/* Payout Date */}
                        {payout.payout_date && (
                          <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                            <ScheduleIcon fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />
                            <Typography variant="body2">
                              {new Date(payout.payout_date).toLocaleDateString()}
                            </Typography>
                          </Box>
                        )}

                        {/* Campaign Name */}
                        {payout.campaign_name && (
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', color: 'text.secondary' }}>
                            <CampaignIcon fontSize="small" sx={{ mr: 1, opacity: 0.7, mt: 0.5 }} />
                            <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                              {payout.campaign_name}
                            </Typography>
                          </Box>
                        )}

                        {/* Interest Rate */}
                        {payout.interest_rate > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                            <TrendingUpIcon fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />
                            <Typography variant="body2">
                              Interest Rate: {payout.interest_rate}%
                            </Typography>
                          </Box>
                        )}

                        {/* Role */}
                        {payout.role && (
                          <Chip
                            label={payout.role}
                            size="small"
                            variant="outlined"
                            sx={{ width: 'fit-content', fontSize: '0.7rem' }}
                          />
                        )}

                        {/* BlitzPay Badge */}
                        {payout.blitzpay && (
                          <Chip
                            label="BlitzPay"
                            size="small"
                            color="primary"
                            variant="filled"
                            sx={{ width: 'fit-content', fontSize: '0.7rem', fontWeight: 600 }}
                          />
                        )}

                        {/* Claimed Badge */}
                        {payout.metadata?.claimed_by_agency && (
                          <Alert severity="success" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                            Claimed on {new Date(payout.metadata.claimed_by_agency.claimed_at).toLocaleDateString()}
                          </Alert>
                        )}

                        {/* Withdrawn Badge */}
                        {payout.withdrawal_id && (
                          <Alert severity="info" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                            Withdrawn by creator
                          </Alert>
                        )}

                        {/* Notes */}
                        {payout.notes && (
                          <Box 
                            sx={{ 
                              mt: 1,
                              p: 1.5, 
                              backgroundColor: alpha(theme.palette.info.main, 0.08),
                              borderRadius: 2,
                              borderLeft: `3px solid ${theme.palette.info.main}`
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                              <DescriptionIcon fontSize="small" sx={{ mr: 1, opacity: 0.7, color: 'info.main', mt: 0.3 }} />
                              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                                {payout.notes}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Stack>
                    </CardContent>

                    {/* Actions */}
                    <CardActions
                      sx={{
                        justifyContent: 'space-between',
                        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        px: 2,
                        py: 1.5,
                        background: alpha(theme.palette.background.default, 0.3),
                        flexWrap: 'wrap',
                        gap: 1
                      }}
                    >
                      {/* Claim Button for Paid/Funded Invoices */}
                      {isClaimable(payout) && (
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          onClick={() => handleOpenClaimDialog(payout)}
                          disabled={claimingPayoutId === payout.invoice_id}
                          startIcon={claimingPayoutId === payout.invoice_id ? <CircularProgress size={16} /> : <DownloadIcon />}
                          sx={{ fontWeight: 600 }}
                        >
                          {claimingPayoutId === payout.invoice_id ? 'Claiming...' : 'Claim to Balance'}
                        </Button>
                      )}

                      {/* Confirm Payment Button for Pending */}
                      {payout.status?.toLowerCase() === 'pending' && payout.has_creator_user && (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => handleOpenActionDialog(payout)}
                          sx={{ fontWeight: 600 }}
                          startIcon={<CheckCircleIcon />}
                        >
                          Confirm Payment
                        </Button>
                      )}

                      {/* View Details for Other Cases */}
                      {!isClaimable(payout) && payout.status?.toLowerCase() !== 'pending' && (
                        <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                          {payout.metadata?.claimed_by_agency ? 'Already claimed' : 'No actions available'}
                        </Typography>
                      )}
                    </CardActions>
                  </ModernCard>
                </Grid>
              </Grow>
            ))
          )}
        </Grid>

        {/* Payment Confirmation Dialog */}
        <Dialog
          open={actionDialogOpen}
          onClose={handleCloseActionDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.background.default, 0.95)} 100%)`,
              backdropFilter: 'blur(20px)',
            }
          }}
        >
          <DialogTitle 
            sx={{ 
              pb: 1,
              fontWeight: 700,
              fontSize: '1.5rem'
            }}
          >
            Confirm Payment
          </DialogTitle>
          
          <Divider />
          
          <DialogContent sx={{ pt: 3 }}>
            {/* Payout Info Card */}
            <GlassmorphicPaper sx={{ p: 2, mb: 3 }}>
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Payout ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedPayout?.po_number || `#${selectedPayout?.id}`}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Amount
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                    ${selectedPayout?.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                </Box>

                {selectedPayout?.campaign_name && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Campaign
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {selectedPayout.campaign_name}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </GlassmorphicPaper>

            {/* Payment Method Selection */}
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Select Payment Method
            </Typography>
            
            <RadioGroup
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              <GlassmorphicPaper 
                sx={{ 
                  p: 2, 
                  mb: 1.5,
                  cursor: 'pointer',
                  border: paymentMethod === 'NET30' ? `2px solid ${theme.palette.primary.main}` : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  transition: 'all 0.2s',
                }}
                onClick={() => setPaymentMethod('NET30')}
              >
                <FormControlLabel
                  value="NET30"
                  control={<Radio />}
                  label={
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccountBalanceIcon fontSize="small" color="primary" />
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          NET30
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Standard 30-day net payment terms
                      </Typography>
                    </Box>
                  }
                  sx={{ width: '100%', m: 0 }}
                />
              </GlassmorphicPaper>

              <GlassmorphicPaper 
                sx={{ 
                  p: 2,
                  cursor: 'pointer',
                  border: paymentMethod === 'BLITZPAY' ? `2px solid ${theme.palette.primary.main}` : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  transition: 'all 0.2s',
                }}
                onClick={() => setPaymentMethod('BLITZPAY')}
              >
                <FormControlLabel
                  value="BLITZPAY"
                  control={<Radio />}
                  label={
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AttachMoneyIcon fontSize="small" color="success" />
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          BlitzPay
                        </Typography>
                        <Chip label="Instant" size="small" color="success" sx={{ height: 20 }} />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Immediate payment processing
                      </Typography>
                    </Box>
                  }
                  sx={{ width: '100%', m: 0 }}
                />
              </GlassmorphicPaper>
            </RadioGroup>
          </DialogContent>

          <Divider />

          <DialogActions sx={{ p: 2.5, gap: 1 }}>
            <Button 
              onClick={handleCloseActionDialog}
              variant="outlined"
              sx={{ fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAction}
              variant="contained"
              color="primary"
              disabled={confirmMutation.isLoading}
              startIcon={confirmMutation.isLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
              sx={{ fontWeight: 600, minWidth: 120 }}
            >
              {confirmMutation.isLoading ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Claim Confirmation Dialog */}
        <Dialog
          open={claimDialogOpen}
          onClose={handleCloseClaimDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.background.default, 0.95)} 100%)`,
              backdropFilter: 'blur(20px)',
            }
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: 700,
              fontSize: '1.5rem'
            }}
          >
            Claim Invoice to Agency Balance
          </DialogTitle>

          <Divider />

          <DialogContent sx={{ pt: 3 }}>
            <Stack spacing={2}>
              <Typography variant="body1">
                You're about to claim this invoice on behalf of <strong>{creatorData?.creator?.creator}</strong>.
              </Typography>
              
              <GlassmorphicPaper sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.1) }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700 }}>
                  Claim Details:
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Amount:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      ${selectedPayout?.amount}
                    </Typography>
                  </Box>
                  {selectedPayout?.campaign_name && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Campaign:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedPayout.campaign_name}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </GlassmorphicPaper>

              <Alert severity="info" icon={<InfoIcon />}>
                <Typography variant="body2">
                  <strong>What happens when you claim:</strong>
                </Typography>
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    • Funds transfer directly to your agency's Global User balance
                  </Typography>
                  <Typography variant="body2">
                    • Creator will NOT receive these funds automatically
                  </Typography>
                  <Typography variant="body2">
                    • You manage payment to the creator separately
                  </Typography>
                  <Typography variant="body2">
                    • Once claimed or withdrawn by creator, cannot be claimed again
                  </Typography>
                  <Typography variant="body2">
                    • This action creates an audit trail in the system
                  </Typography>
                </Stack>
              </Alert>
            </Stack>
          </DialogContent>

          <Divider />

          <DialogActions sx={{ p: 2.5, gap: 1 }}>
            <Button 
              onClick={handleCloseClaimDialog}
              variant="outlined"
              disabled={claimMutation.isLoading}
              sx={{ fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmClaim}
              variant="contained"
              color="success"
              disabled={claimMutation.isLoading}
              startIcon={claimMutation.isLoading ? <CircularProgress size={20} /> : <DownloadIcon />}
              sx={{ fontWeight: 600, minWidth: 140 }}
            >
              {claimMutation.isLoading ? 'Claiming...' : 'Confirm Claim'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbarSeverity} 
            variant="filled"
            sx={{ 
              width: '100%',
              borderRadius: 2,
              fontWeight: 600
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </Fade>
  );
}